import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable max-lines */
import { useEffect, useRef, useState, useCallback, } from "react";
import debounce from "lodash/debounce";
import "cleave.js/dist/addons/cleave-phone.ru";
import { InputContainer, InputErrorMessage, InputField, InputLabel, InputMarksContainer, InputPrefix, InputSliderContainer, InputSuffix, InputWrapperContainer, StyledCleave, StyledPhoneInput, } from "./styled";
import { Flex, FlexSpan } from "../Grid";
import { Slider } from "../Slider";
import { isValidDate, isValidEmail, isValidInn, isValidOgrn, isValidPhone, isValidUrl } from "../../utils";
const InputWrapper = ({ prefix, suffix, inputRenderer, disabled, noDisabledBackground, error, errorMessage, className, hideArrows, style, height, width, isFocused, label, labelExtra, labelPosition = "top", inputSiblings, marks, }) => (_jsxs(InputWrapperContainer, { width: width, children: [_jsxs(Flex, { alignItems: labelPosition !== "top" ? "center" : undefined, flexDirection: labelPosition === "top"
                ? "column"
                : labelPosition === "right"
                    ? "row-reverse"
                    : "row", children: [label && (_jsx(FlexSpan, { onClick: e => e.stopPropagation(), mr: labelPosition === "left" ? "1rem" : undefined, ml: labelPosition === "right" ? "1rem" : undefined, textAlign: labelPosition === "left" ? "right" : undefined, children: _jsxs(InputLabel, { children: [_jsx(FlexSpan, { children: label }), labelExtra && (_jsx(FlexSpan, { display: "flex", ml: "0.5rem", children: labelExtra }))] }) })), _jsxs(FlexSpan, { children: [_jsxs(InputContainer, { disabled: disabled, noDisabledBackground: noDisabledBackground, className: className, hideArrows: hideArrows, style: style, width: width, height: height, error: error, isFocused: isFocused, children: [prefix && _jsx(InputPrefix, { children: prefix }), inputRenderer, suffix && _jsx(InputSuffix, { children: suffix })] }), inputSiblings] })] }), marks?.filter(item => item !== undefined).length > 0 && (_jsx(InputMarksContainer, { children: marks?.filter(item => item !== undefined).map((mark, markIndex) => (_jsx(FlexSpan, { children: mark }, `mark-${markIndex}`))) })), error && errorMessage && _jsx(InputErrorMessage, { onClick: e => e.stopPropagation(), children: errorMessage })] }));
const InputRenderer = props => {
    const inputRef = useRef(null);
    const dateInputRef = useRef();
    const { name, value, defaultValue, placeholder, onEnter, onChange, onKeyDown, onFocusChange, onFocus, onBlur, type, disabled, autofocus, min, max, maxLength, disableInputOnly, error, inputStyle, round, regexp, } = props;
    const [ownValue, setOwnValue] = useState(value || defaultValue || "");
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        defaultValue && setOwnValue(defaultValue);
    }, [defaultValue]);
    useEffect(() => {
        onFocusChange?.(focused);
    }, [focused, onFocusChange]);
    useEffect(() => {
        if (inputRef.current && autofocus) {
            inputRef.current.focus();
        }
    }, [inputRef, autofocus]);
    if (type === "number") {
        return (_jsx(StyledCleave, { options: {
                numeral: true,
                delimiter: " ",
                numeralDecimalMark: ".",
            }, name: name, value: value !== undefined ? value : ownValue, placeholder: placeholder, style: inputStyle, min: min, max: max, error: error, disabled: disabled, onFocus: () => {
                setFocused(true);
                onFocus?.();
            }, onBlur: () => {
                setFocused(false);
                onBlur?.();
            }, onKeyDown: (e) => {
                if (disableInputOnly) {
                    return;
                }
                if (e.key === "Enter" && typeof onEnter === "function") {
                    e.preventDefault();
                    return onEnter();
                }
                onKeyDown?.(e);
            }, onChange: (e) => {
                if (disableInputOnly) {
                    return;
                }
                let currentValue = e.target.rawValue;
                if (min !== undefined && (currentValue === "" || +currentValue < min)) {
                    currentValue = min.toString();
                }
                else if (max !== undefined && ownValue !== undefined && +ownValue > max) {
                    currentValue = max.toString();
                }
                else if (round && ownValue !== undefined && ownValue !== "") {
                    currentValue = Math.round(+ownValue).toString();
                }
                setOwnValue(currentValue);
                onChange?.(currentValue !== "" ? parseFloat(currentValue) : "");
            } }));
    }
    if (type === "phone") {
        return (_jsx(StyledPhoneInput, { name: name, international: true, value: value ?? ownValue, placeholder: placeholder, error: error, disabled: disabled, onFocus: () => {
                setFocused(true);
                onFocus?.();
            }, onBlur: () => {
                setFocused(false);
                onBlur?.();
            }, onChange: (e) => {
                if (disableInputOnly) {
                    return;
                }
                setOwnValue(e);
                onChange?.(e);
            } }));
    }
    if (type === "date") {
        return (_jsx(StyledCleave, { ref: dateInputRef, options: {
                date: true,
                delimiter: ".",
                datePattern: ["d", "m", "Y"],
            }, name: name, value: value !== undefined ? (value || "") : ownValue, placeholder: placeholder, style: inputStyle, error: error, disabled: disabled, onFocus: () => {
                setFocused(true);
                onFocus?.();
            }, onBlur: () => {
                setFocused(false);
                onBlur?.();
            }, onKeyDown: (e) => {
                if (disableInputOnly) {
                    return;
                }
                if (e.key === "Enter" && typeof onEnter === "function") {
                    e.preventDefault();
                    return onEnter();
                }
                onKeyDown?.(e);
            }, onChange: (e) => {
                if (disableInputOnly) {
                    return;
                }
                const formattedValue = dateInputRef.current.getISOFormatDate();
                let currentValue = e.target.rawValue;
                if (formattedValue) {
                    const parsedDate = formattedValue.split("-");
                    currentValue = `${parsedDate[2]}.${parsedDate[1]}.${parsedDate[0]}`;
                }
                setOwnValue(currentValue);
                onChange?.(currentValue);
            } }));
    }
    return (_jsx(InputField, { ref: inputRef, type: type, name: name, value: value !== undefined ? value : ownValue, onFocus: () => {
            setFocused(true);
            onFocus?.();
        }, onBlur: () => {
            setFocused(false);
            onBlur?.();
        }, placeholder: placeholder, maxLength: maxLength, style: inputStyle, error: error, disabled: disabled, onKeyDown: (e) => {
            if (disableInputOnly) {
                return;
            }
            if (e.key === "Enter" && typeof onEnter === "function") {
                e.preventDefault();
                return onEnter();
            }
            onKeyDown?.(e);
        }, onChange: (e) => {
            if (disableInputOnly) {
                return;
            }
            let currentValue = e.target.value;
            if (type === "text" && !!regexp) {
                currentValue = (currentValue.match(new RegExp(regexp, "gi")) || []).join("");
            }
            setOwnValue(currentValue);
            onChange?.(currentValue);
        } }));
};
const TextInput = props => {
    const [focused, setFocused] = useState(false);
    return (_jsx(InputWrapper, { inputRenderer: _jsx(InputRenderer, { type: "text", ...props, onFocusChange: setFocused }), isFocused: focused, ...props }));
};
const NumberInput = props => {
    const [focused, setFocused] = useState(false);
    return (_jsx(InputWrapper, { inputRenderer: _jsx(InputRenderer, { type: "number", ...props, onFocusChange: setFocused }), isFocused: focused, ...props }));
};
const PasswordInput = props => {
    const [focused, setFocused] = useState(false);
    return (_jsx(InputWrapper, { inputRenderer: _jsx(InputRenderer, { type: "password", ...props, onFocusChange: setFocused }), isFocused: focused, ...props }));
};
const PhoneInput = ({ innerKey, onBlur, onChange, value, error: outerError, errorMessage, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        setError(!isValidPhone(value));
    }, [innerKey]); // eslint-disable-line
    useEffect(() => {
        if (!focused) {
            setError(!isValidPhone(value));
        }
    }, [value]); // eslint-disable-line
    return (_jsx(InputWrapper, { inputRenderer: (_jsx(InputRenderer, { type: "phone", value: value, onBlur: () => {
                if (!isValidPhone(value)) {
                    setError(true);
                }
                onBlur?.();
            }, onChange: (inputValue) => {
                setError(false);
                onChange?.(inputValue);
            }, ...props, onFocusChange: setFocused })), isFocused: focused, error: outerError ?? error, errorMessage: errorMessage ?? "Введите корректный номер телефона", ...props }));
};
const InnInput = ({ innerKey, onBlur, onChange, value, error: outerError, errorMessage, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        setError(!isValidInn(value));
    }, [innerKey]); // eslint-disable-line
    useEffect(() => {
        if (!focused) {
            setError(!isValidInn(value));
        }
    }, [value]); // eslint-disable-line
    return (_jsx(InputWrapper, { inputRenderer: (_jsx(InputRenderer, { type: "text", maxLength: 12, regexp: "[0-9]+", value: value, onBlur: () => {
                if (!isValidInn(value)) {
                    setError(true);
                }
                onBlur?.();
            }, onChange: (inputValue) => {
                setError(false);
                onChange?.(inputValue);
            }, ...props, onFocusChange: setFocused })), isFocused: focused, error: outerError ?? error, errorMessage: errorMessage ?? "Введите корректный ИНН", ...props }));
};
const EmailInput = ({ innerKey, onBlur, onChange, value, error: outerError, errorMessage, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        setError(!isValidEmail(value));
    }, [innerKey]); // eslint-disable-line
    useEffect(() => {
        if (!focused) {
            setError(!isValidEmail(value));
        }
    }, [value]); // eslint-disable-line
    return (_jsx(InputWrapper, { inputRenderer: (_jsx(InputRenderer, { type: "text", value: value, maxLength: 50, onBlur: () => {
                if (!isValidEmail(value)) {
                    setError(true);
                }
                onBlur?.();
            }, onChange: (inputValue) => {
                setError(false);
                onChange?.(inputValue);
            }, ...props, onFocusChange: setFocused })), isFocused: focused, error: outerError ?? error, errorMessage: errorMessage ?? "Введите корректную электронную почту", ...props }));
};
const UrlInput = ({ innerKey, onBlur, onChange, value, error: outerError, errorMessage, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        setError(!isValidUrl(value));
    }, [innerKey]); // eslint-disable-line
    useEffect(() => {
        if (!focused) {
            setError(!isValidUrl(value));
        }
    }, [value]); // eslint-disable-line
    return (_jsx(InputWrapper, { inputRenderer: (_jsx(InputRenderer, { type: "text", value: value, maxLength: 50, onBlur: () => {
                if (!isValidUrl(value)) {
                    setError(true);
                }
                onBlur?.();
            }, onChange: (inputValue) => {
                setError(false);
                onChange?.(inputValue);
            }, ...props, onFocusChange: setFocused })), isFocused: focused, error: outerError ?? error, errorMessage: errorMessage ?? "Введите корректный адрес сайта", ...props }));
};
const OgrnInput = ({ innerKey, onBlur, onChange, value, error: outerError, errorMessage, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        setError(!isValidOgrn(value));
    }, [innerKey]); // eslint-disable-line
    useEffect(() => {
        if (!focused) {
            setError(!isValidOgrn(value));
        }
    }, [value]); // eslint-disable-line
    return (_jsx(InputWrapper, { inputRenderer: (_jsx(InputRenderer, { type: "text", value: value, maxLength: 15, regexp: "[0-9]+", onBlur: () => {
                if (!isValidOgrn(value)) {
                    setError(true);
                }
                onBlur?.();
            }, onChange: (inputValue) => {
                setError(false);
                onChange?.(inputValue);
            }, ...props, onFocusChange: setFocused })), isFocused: focused, error: outerError ?? error, errorMessage: errorMessage ?? "Введите корректный ОГРН/ОГРНИП", ...props }));
};
const DateInput = ({ innerKey, onBlur, onChange, value, error: outerError, maxDate, errorMessage, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        setError(!isValidDate(value, { max: maxDate }));
    }, [innerKey]); // eslint-disable-line
    useEffect(() => {
        if (!focused) {
            setError(!isValidDate(value, { max: maxDate }));
        }
    }, [value]); // eslint-disable-line
    return (_jsx(InputWrapper, { inputRenderer: _jsx(InputRenderer, { type: "date", value: value, onBlur: () => {
                if (!isValidDate(value, { max: maxDate })) {
                    setError(true);
                }
                onBlur?.();
            }, onChange: (inputValue) => {
                setError(false);
                onChange?.(inputValue);
            }, ...props, onFocusChange: setFocused }), isFocused: focused, error: outerError ?? error, errorMessage: errorMessage ?? "Введите корректную дату", ...props }));
};
const InputSlider = ({ min, max, defaultValue, step, noMarks, disabled, disableInput, markFormatFunc, onChange, delay, value: inputValue, ...props }) => {
    const [value, setValue] = useState(defaultValue);
    const lazySyncValues = useCallback(debounce((outerValue) => {
        onChange && onChange(outerValue);
    }, delay ?? 60), [onChange, delay]);
    useEffect(() => {
        setValue(+inputValue);
    }, [inputValue]);
    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]); // eslint-disable-line
    return (_jsx(InputSliderContainer, { children: _jsx(NumberInput, { disabled: disabled || disableInput, defaultValue: defaultValue?.toString(), value: value?.toString(), min: min, max: max, onChange: (e) => {
                if (e === undefined) {
                    setValue(min);
                    onChange?.(min);
                }
                else {
                    setValue(e);
                    onChange?.(e);
                }
            }, hideArrows: true, inputSiblings: (_jsx(Slider, { min: min, max: max, step: step, disabled: disabled, noMarks: noMarks, value: value, onChange: (e) => {
                    setValue(e);
                    lazySyncValues(e);
                }, style: { marginTop: "-8px" }, noRail: true, markFormatFunc: markFormatFunc })), ...props }) }));
};
export { TextInput, NumberInput, PasswordInput, PhoneInput, InnInput, EmailInput, UrlInput, OgrnInput, DateInput, InputSlider, };
